import { Inject, Injectable } from '@angular/core';
import { USER_GROUP_QUERIES, UserGroupQueries } from '@/app/platform/platform-data/queries/types';
import { USER_GROUP_SERVICE, UserGroupService } from '@/app/platform/platform-data/services/types';
import { err, ok } from '@common/result/Result';

import { AsyncResult } from '@common/result/asyncResult';
import { UserGroupEntityStore } from '@/app/platform/platform-data/store/user-group.entity.store';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import { UserAccountEntity } from '@/app/platform/platform-data/models/user-account.entity';
import {
  EGI_APP_ACTIONS,
  EgiAppActions,
  UserGroupActions
} from '@/app/platform/platform-data/actions/types';

@Injectable()
export class UserGroupStoreActions implements UserGroupActions {
  constructor(
    private readonly userGroupStore: UserGroupEntityStore,
    @Inject(USER_GROUP_SERVICE) private readonly userGroupService: UserGroupService,
    @Inject(USER_GROUP_QUERIES) private readonly userGroupQueries: UserGroupQueries,
    @Inject(EGI_APP_ACTIONS) private readonly egiAppActions: EgiAppActions
  ) {}

  async create(m: UserGroupEntity): AsyncResult<UserGroupEntity> {
    const result = await this.userGroupService.create(m);
    result.ifSuccess(userGroup => this.userGroupStore.add(userGroup));
    return result;
  }

  async delete(m: UserGroupEntity): AsyncResult<UserGroupEntity> {
    const result = await this.userGroupService.delete(m);
    result.ifSuccess(userGroup => this.userGroupStore.remove(userGroup.id));
    return result;
  }

  async loadAll(): AsyncResult<UserGroupEntity[]> {
    const result = await this.egiAppActions.loadAll();
    if (result.hasFailed) {
      return err(result.errors);
    }
    const hasCache = this.userGroupQueries.getHasCache();
    if (hasCache) {
      const userGroups = this.userGroupQueries.getAll();
      return ok(userGroups);
    }

    return this.reloadAll();
  }

  async reloadAll(): AsyncResult<UserGroupEntity[]> {
    const result = await this.userGroupService.getAll();
    result.ifSuccess(userGroups => this.userGroupStore.set(userGroups));
    return result;
  }

  async reloadOne(id: string): AsyncResult<UserGroupEntity> {
    const result = await this.userGroupService.getById(id);
    result.ifSuccess(group => this.userGroupStore.update(id, group));
    return result;
  }

  async update(m: UserGroupEntity): AsyncResult<UserGroupEntity> {
    const result = await this.userGroupService.update(m);
    result.ifSuccess(userGroup => this.userGroupStore.upsert(userGroup.id, userGroup));
    return result;
  }

  async loadLinkedApps(userGroup: UserGroupEntity): AsyncResult<UserGroupEntity> {
    const { id } = userGroup;
    const result = await this.userGroupService.getLinkedAppIds(id);

    result.ifSuccess(ids => this.userGroupStore.setAppLinksForGroup(id, ids));
    if (!result.isValid) {
      return err(result.errors);
    }
    return ok(userGroup);
  }

  async loadOne(id: string): AsyncResult<UserGroupEntity> {
    const existing = this.userGroupQueries.one(id);
    if (existing) {
      return ok(existing);
    }

    return await this.reloadOne(id);
  }

  async addMember(
    group: UserGroupEntity,
    account: UserAccountEntity
  ): AsyncResult<UserGroupEntity> {
    const result = await this.userGroupService.addMember(group, account);
    result.ifSuccess(g => {
      this.userGroupStore.update(g.id, g);
    });
    return result;
  }

  async removeMember(
    group: UserGroupEntity,
    account: UserAccountEntity
  ): AsyncResult<UserGroupEntity> {
    const result = await this.userGroupService.removeMember(group, account);
    result.ifSuccess(g => {
      this.userGroupStore.update(g.id, g);
    });

    return result;
  }

  reset(): void {
    this.userGroupStore.reset();
  }
}
